import Tippy from '@tippyjs/react'
import { type ReactNode, type SyntheticEvent, useEffect, useState } from 'react'
import { classNameParser } from '../../../../helpers'
import type { InputProps } from '../../../../types'

import '../../../../styles/input.css'
import './style.css'

import helpImage from '../../../../images/ic-help.svg'

type TextInputProps = InputProps<
  string | number,
  {
    inputMode?: 'search' | 'text' | 'email' | 'tel' | 'url' | 'none' | 'numeric' | 'decimal' | undefined
    fieldInfoSize?: 'small' | 'large'
    fieldInfo?: ReactNode
    autoFillEnable?: boolean
    hardFillValue?: string | number
    type?: string
  }
>

export const TextInput = (props: TextInputProps) => {
  const [infoTarget, setInfoTarget] = useState<HTMLElement>()
  const [fieldTarget, setFieldTarget] = useState<HTMLElement>()
  const [value, setValue] = useState<string | number>(props.value || '')

  const handleValueChange = (e: SyntheticEvent<HTMLInputElement>) => {
    setValue(e.currentTarget.value)
    if (props.onChange) props.onChange(e.currentTarget.value)
  }

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (props.onFocus) props.onFocus(e)
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (props.onBlur) props.onBlur(e)
  }

  useEffect(() => {
    if (props.hardFillValue !== undefined) {
      setValue(props.hardFillValue)
    }
  }, [props.hardFillValue])

  return (
    <>
      <Tippy
        reference={fieldTarget}
        triggerTarget={infoTarget}
        disabled={!props.fieldInfo}
        content={
          <div className="global--tooltip-content bg-white w-full rounded-md shadow-2xl px-[16px] py-[4px]">
            {props.fieldInfo}
          </div>
        }
      />
      <div
        ref={(el) => {
          if (el) setFieldTarget(el)
        }}
        className={classNameParser('global--input-container input--text-input-container', props.className)}
        id={props.id}
        key={props.key}
        style={props.style}
      >
        <div className="absolute right-0 top-[-20px] flex items-center">
          {props.optional === true ? (
            <p className={`global--input-label font-400 !text-[12px]`}>Optional field</p>
          ) : null}
        </div>
        <input
          type={props.type || 'text'}
          className={`global--input ${value ? 'global--with-value' : ''} ${
            props.fieldInfo !== undefined ? '!pr-[35px]' : ''
          } ${!props.autoComplete ? 'hide-autocomplete' : ''}`}
          onChange={props.autoFillEnable === true ? undefined : handleValueChange}
          onInput={props.autoFillEnable === true ? handleValueChange : undefined}
          onFocus={handleFocus}
          onBlur={handleBlur}
          value={value}
          // biome-ignore lint/a11y/noAutofocus: opt-in through the autoFocus prop
          autoFocus={props.autoFocus}
          disabled={props.disabled}
          inputMode={props.inputMode || 'text'}
          placeholder={props.placeHolder}
          {...(props.minLength ? { minLength: props.minLength } : {})}
          {...(props.maxLength ? { maxLength: props.maxLength } : {})}
        />
        {props.fieldInfo !== undefined ? (
          <div
            ref={(el) => {
              if (el) setInfoTarget(el)
            }}
            className="absolute right-0 top-0"
          >
            <img src={helpImage.src} alt="help icon" width={48} height={48}></img>
          </div>
        ) : null}
        {props.label && (
          <label
            htmlFor={props.id}
            className={`input--text-input-label ${props.fieldInfo ? 'has-tooltip' : ''}`}
            title={props.label}
          >
            <span className="input--text-input-label-text">{props.label}</span>
          </label>
        )}
        {props.error && <p className="global--input-error">{props.error}</p>}
      </div>
    </>
  )
}
